import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import { unwrapData, unwrapPaginated } from "@/lib/api-response";
import type { ApiSuccessResponse, PaginatedResult } from "@/types/common";
import type {
  AssignRoleRequest,
  CreateUserRequest,
  CreateUserResponse,
  InviteUserRequest,
  ListUsersQuery,
  UpdateUserRequest,
  User,
  UserActivityQuery,
} from "@/types/user";
import type { AuditLog } from "@/types/audit-log";

export type UserMutationResult<T> = {
  data: T;
  message?: string;
};

export type CreateUserMutationResult = UserMutationResult<CreateUserResponse>;

function unwrapMutation<T>(response: ApiSuccessResponse<T>): UserMutationResult<T> {
  return {
    data: unwrapData<T>(response),
    message: response.message,
  };
}

function toApiPaginationParams<T extends { page?: number; limit?: number }>(
  query?: T,
) {
  if (!query) return undefined;
  const { limit, page, ...rest } = query;
  return {
    ...rest,
    ...(page !== undefined ? { page } : {}),
    ...(limit !== undefined ? { pageSize: limit } : {}),
  };
}

export const userService = {
  async list(query?: ListUsersQuery): Promise<PaginatedResult<User>> {
    const response = await http.get(API_ENDPOINTS.USERS.LIST, {
      params: toApiPaginationParams(query),
    });
    return unwrapPaginated<User>(response);
  },

  async create(payload: CreateUserRequest): Promise<CreateUserMutationResult> {
    const response = await http.post<ApiSuccessResponse<CreateUserResponse>>(
      API_ENDPOINTS.USERS.CREATE,
      payload,
    );
    return unwrapMutation(response);
  },

  async get(id: string): Promise<User> {
    const response = await http.get(API_ENDPOINTS.USERS.DETAIL(id));
    return unwrapData<User>(response);
  },

  async update(id: string, payload: UpdateUserRequest): Promise<User> {
    const response = await http.put(API_ENDPOINTS.USERS.DETAIL(id), payload);
    return unwrapData<User>(response);
  },

  /** Soft delete (Super Admin): hides the user from the list; the record stays in the database. */
  async remove(id: string): Promise<void> {
    await http.post(API_ENDPOINTS.USERS.DELETE(id));
  },

  async deactivate(id: string): Promise<User> {
    const response = await http.patch(API_ENDPOINTS.USERS.DEACTIVATE(id));
    return unwrapData<User>(response);
  },

  async assignRole(id: string, payload: AssignRoleRequest): Promise<User> {
    const response = await http.patch(API_ENDPOINTS.USERS.ROLE(id), payload);
    return unwrapData<User>(response);
  },

  async invite(payload: InviteUserRequest): Promise<UserMutationResult<User>> {
    const response = await http.post<ApiSuccessResponse<User>>(
      API_ENDPOINTS.USERS.INVITE,
      payload,
    );
    return unwrapMutation(response);
  },

  async activity(
    id: string,
    query?: UserActivityQuery,
  ): Promise<PaginatedResult<AuditLog>> {
    const response = await http.get(API_ENDPOINTS.USERS.ACTIVITY(id), {
      params: toApiPaginationParams(query),
    });
    return unwrapPaginated<AuditLog>(response);
  },
};
